/**
 * PBR material library — Poly Haven CC0 texture sets (self-hosted in /textures).
 * Quake 3 palette: riveted metal floors, dark panel walls, rusted grates, concrete, rock,
 * plus station hull panels, blue anodised plates, painted metal, concrete panels.
 * Every material gets diff / normal / rough / (metal) / AO maps with tiling.
 */

import * as THREE from 'three';

export type MaterialName =
  | 'floor' | 'wall' | 'grate' | 'concrete' | 'rock' | 'trim'
  | 'panel' | 'hull' | 'grate2' | 'painted' | 'cpanel';

interface SetDef { dir: string; metal: boolean; metalness?: number; roughnessBias?: number }

const SETS: Record<Exclude<MaterialName, 'trim'>, SetDef> = {
  floor:    { dir: 'metal_plate',        metal: true },
  wall:     { dir: 'metal_plate_02',     metal: true },
  grate:    { dir: 'rusty_metal_grid',   metal: false, metalness: 0.4 },
  grate2:   { dir: 'metal_grate_rusty',  metal: true },
  concrete: { dir: 'concrete_wall_006',  metal: false },
  cpanel:   { dir: 'concrete_panels',    metal: false },
  rock:     { dir: 'rock_wall_08',       metal: false },
  panel:    { dir: 'blue_metal_plate',   metal: false, metalness: 0.85, roughnessBias: -0.15 },
  hull:     { dir: 'factory_wall',       metal: false, metalness: 0.55 },
  painted:  { dir: 'rusty_painted_metal', metal: false, metalness: 0.5 },
};

const loader = new THREE.TextureLoader();

/**
 * Cache de textures : UNE seule image GL par fichier.
 * Le tiling (repeat) n'entre pas dans la clé de cache GPU de three : les
 * variantes sont de simples `clone()` qui partagent la même `source`, donc
 * la même WebGLTexture. Ne JAMAIS appeler dispose() sur un clone : cela
 * détruirait la texture partagée par toutes les variantes.
 */
interface TexEntry { variants: Map<string, THREE.Texture>; loaded: boolean }
const cache = new Map<string, TexEntry>();
let maxAniso = 8;

/** Call once with the renderer so textures use the best anisotropy available. */
export function configureTextures(renderer: THREE.WebGLRenderer): void {
  maxAniso = Math.min(16, renderer.capabilities.getMaxAnisotropy());
  for (const e of cache.values()) {
    for (const t of e.variants.values()) { t.anisotropy = maxAniso; if (e.loaded) t.needsUpdate = true; }
  }
}

function tex(path: string, srgb: boolean, repeat: number, repeatY = repeat): THREE.Texture {
  let e = cache.get(path);
  if (!e) {
    const base = loader.load(path, () => {
      const en = cache.get(path);
      if (!en) return;
      en.loaded = true;
      // l'image est arrivée : réveiller chaque variante (une seule montée GPU)
      for (const v of en.variants.values()) v.needsUpdate = true;
    });
    base.wrapS = base.wrapT = THREE.RepeatWrapping;
    base.anisotropy = maxAniso;
    if (srgb) base.colorSpace = THREE.SRGBColorSpace;
    e = { variants: new Map([['base', base]]), loaded: false };
    cache.set(path, e);
  }
  const key = `${repeat}|${repeatY}`;
  const hit = e.variants.get(key);
  if (hit) return hit;
  const base = e.variants.get('base')!;
  // la première demande réutilise la texture de base, les suivantes la clonent
  const t = e.variants.size === 1 ? base : base.clone();
  if (t !== base && e.loaded) t.needsUpdate = true;
  t.repeat.set(repeat, repeatY);
  e.variants.set(key, t);
  return t;
}

export interface PBROptions {
  repeat?: number;
  repeatY?: number;
  color?: number;
  roughnessBias?: number;
  emissive?: number;
  emissiveIntensity?: number;
  envMapIntensity?: number;
  normalScale?: number;
}

export function pbr(name: MaterialName, opts: PBROptions = {}): THREE.MeshStandardMaterial {
  const repeat = opts.repeat ?? 2;
  const repeatY = opts.repeatY ?? repeat;

  if (name === 'trim') {
    // Emissive Bitcoin-orange trim strips (no textures, pure emissive)
    return new THREE.MeshStandardMaterial({
      color: 0x111111,
      roughness: 0.3,
      metalness: 0.9,
      emissive: new THREE.Color(opts.emissive ?? 0xf7931a),
      emissiveIntensity: opts.emissiveIntensity ?? 2.5,
    });
  }

  const set = SETS[name];
  const base = `${import.meta.env.BASE_URL}textures/${set.dir}`;

  const mat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(opts.color ?? 0xffffff),
    map: tex(`${base}/diff.jpg`, true, repeat, repeatY),
    normalMap: tex(`${base}/nor.jpg`, false, repeat, repeatY),
    roughnessMap: tex(`${base}/rough.jpg`, false, repeat, repeatY),
    metalnessMap: set.metal ? tex(`${base}/metal.jpg`, false, repeat, repeatY) : null,
    aoMap: tex(`${base}/ao.jpg`, false, repeat, repeatY),
    roughness: 1.0 + (opts.roughnessBias ?? set.roughnessBias ?? 0),
    metalness: set.metal ? 1.0 : (set.metalness ?? 0.05),
    envMapIntensity: opts.envMapIntensity ?? 1.0,
    emissive: new THREE.Color(opts.emissive ?? 0x000000),
    emissiveIntensity: opts.emissiveIntensity ?? 0,
  });
  const ns = opts.normalScale ?? 1.0;
  mat.normalScale.set(ns, ns);
  return mat;
}

/** Tinted station glass — reflective, slightly transparent, fog-free so space stays visible. */
export function glass(tint = 0x6ec6ff, opacity = 0.22): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(tint),
    roughness: 0.05,
    metalness: 0.0,
    transparent: true,
    opacity,
    envMapIntensity: 1.6,
    clearcoat: 1.0,
    clearcoatRoughness: 0.05,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
}

/** Solid gunmetal / steel / anodised presets used by weapons and props. */
export const SOLID = {
  gunmetal: () => new THREE.MeshStandardMaterial({ color: 0x1b1d24, roughness: 0.38, metalness: 0.92, envMapIntensity: 1.2 }),
  steel:    () => new THREE.MeshStandardMaterial({ color: 0x9aa0ab, roughness: 0.22, metalness: 1.0, envMapIntensity: 1.4 }),
  brushed:  () => new THREE.MeshStandardMaterial({ color: 0x6c7280, roughness: 0.45, metalness: 1.0, envMapIntensity: 1.0 }),
  carbon:   () => new THREE.MeshStandardMaterial({ color: 0x0f1013, roughness: 0.7, metalness: 0.3 }),
  rubber:   () => new THREE.MeshStandardMaterial({ color: 0x15130f, roughness: 0.95, metalness: 0.0 }),
  orange:   () => new THREE.MeshStandardMaterial({ color: 0xf7931a, roughness: 0.3, metalness: 0.75, envMapIntensity: 1.3 }),
  glow:     (c: number, i = 2.5) => new THREE.MeshStandardMaterial({ color: 0x000000, emissive: new THREE.Color(c), emissiveIntensity: i, roughness: 0.2, metalness: 0 }),
};

/**
 * Procedural deep-space environment — nebula + stars, used both as skybox
 * and as PBR environment map (reflections). Bitcoin orange / electric cyan.
 */
export function createSpaceEnvironment(renderer: THREE.WebGLRenderer): { sky: THREE.Mesh; envMap: THREE.Texture } {
  // Mobile : demi-résolution (2048×1024) + moitié moins d'étoiles → boot ~4× plus rapide
  const touch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
  const size = touch ? 1024 : 1536;
  const starCount = touch ? 3500 : 7000;
  const canvas = document.createElement('canvas');
  canvas.width = size * 2;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Deep space gradient — brighter so it survives ACES tonemapping
  const g = ctx.createLinearGradient(0, 0, 0, size);
  g.addColorStop(0, '#0c1030');
  g.addColorStop(0.35, '#130f34');
  g.addColorStop(0.6, '#0a0a24');
  g.addColorStop(1, '#040318');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size * 2, size);

  // Nebula: layered soft blobs (fBm-like) in the palette
  const palette = ['247,147,26', '0,200,255', '140,60,220', '0,255,220', '255,80,160', '80,120,255'];
  const seeds = [
    { x: 0.18, y: 0.42, r: 0.42, c: 0 }, { x: 0.72, y: 0.38, r: 0.46, c: 1 }, { x: 0.48, y: 0.72, r: 0.36, c: 2 },
    { x: 0.88, y: 0.68, r: 0.3, c: 0 }, { x: 0.06, y: 0.8, r: 0.32, c: 3 }, { x: 0.62, y: 0.16, r: 0.28, c: 4 },
    { x: 0.35, y: 0.2, r: 0.3, c: 5 },
  ];
  for (const b of seeds) {
    // main blob
    let rg = ctx.createRadialGradient(b.x * size * 2, b.y * size, 0, b.x * size * 2, b.y * size, b.r * size);
    rg.addColorStop(0, `rgba(${palette[b.c]},0.30)`);
    rg.addColorStop(0.5, `rgba(${palette[b.c]},0.12)`);
    rg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = rg;
    ctx.fillRect(0, 0, size * 2, size);
    // detail wisps (smaller offset blobs → cloud structure)
    for (let i = 0; i < 14; i++) {
      const ox = (Math.random() - 0.5) * b.r * size * 1.6, oy = (Math.random() - 0.5) * b.r * size * 1.1;
      const rr = b.r * size * (0.12 + Math.random() * 0.3);
      const cx = b.x * size * 2 + ox, cy = b.y * size + oy;
      rg = ctx.createRadialGradient(cx, cy, 0, cx, cy, rr);
      const dark = Math.random() < 0.3;
      rg.addColorStop(0, dark ? 'rgba(4,3,16,0.35)' : `rgba(${palette[b.c]},${0.08 + Math.random() * 0.14})`);
      rg.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = rg;
      ctx.fillRect(cx - rr, cy - rr, rr * 2, rr * 2);
    }
  }

  // Milky way band — diagonal soft cloud
  ctx.save();
  ctx.translate(size, size / 2);
  ctx.rotate(-0.5);
  for (let i = 0; i < 90; i++) {
    const x = (Math.random() - 0.5) * size * 3.2;
    const y = (Math.random() - 0.5) * size * 0.28;
    const r = 60 + Math.random() * 180;
    const rg = ctx.createRadialGradient(x, y, 0, x, y, r);
    rg.addColorStop(0, `rgba(150,170,255,${0.03 + Math.random() * 0.05})`);
    rg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = rg;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  ctx.restore();

  // THE BITCOIN SUN — glowing orange planet with ring (upper left focal point)
  const bx = size * 0.30, by = size * 0.30, br = size * 0.17;
  let rg = ctx.createRadialGradient(bx, by, br * 0.5, bx, by, br * 3.2);
  rg.addColorStop(0, 'rgba(247,147,26,0.5)');
  rg.addColorStop(0.35, 'rgba(247,147,26,0.18)');
  rg.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = rg;
  ctx.fillRect(bx - br * 3.2, by - br * 3.2, br * 6.4, br * 6.4);
  const pg = ctx.createRadialGradient(bx - br * 0.4, by - br * 0.4, br * 0.1, bx, by, br);
  pg.addColorStop(0, '#ffdf9e');
  pg.addColorStop(0.55, '#f7931a');
  pg.addColorStop(0.85, '#a34e00');
  pg.addColorStop(1, '#5c2800');
  ctx.fillStyle = pg;
  ctx.beginPath(); ctx.arc(bx, by, br, 0, Math.PI * 2); ctx.fill();
  ctx.save();
  ctx.beginPath(); ctx.arc(bx, by, br, 0, Math.PI * 2); ctx.clip();
  for (let i = 0; i < 11; i++) {
    const y = by - br + (i + 0.5) * (br * 2 / 11) + Math.sin(i * 2.4) * br * 0.08;
    ctx.fillStyle = `rgba(150,80,0,${0.12 + (i % 3) * 0.05})`;
    ctx.beginPath();
    ctx.ellipse(bx, y, br * 1.05, br * (0.05 + (i % 3) * 0.02), 0, 0, Math.PI * 2);
    ctx.fill();
  }
  // terminator shadow
  const sh = ctx.createLinearGradient(bx - br, by, bx + br, by);
  sh.addColorStop(0, 'rgba(0,0,0,0)'); sh.addColorStop(0.6, 'rgba(0,0,0,0.05)'); sh.addColorStop(1, 'rgba(0,0,0,0.55)');
  ctx.fillStyle = sh; ctx.fillRect(bx - br, by - br, br * 2, br * 2);
  ctx.restore();
  ctx.save();
  ctx.translate(bx, by);
  ctx.rotate(-0.35);
  ctx.strokeStyle = 'rgba(255,200,120,0.85)';
  ctx.lineWidth = br * 0.07;
  ctx.beginPath(); ctx.ellipse(0, 0, br * 1.75, br * 0.55, 0, 0, Math.PI * 2); ctx.stroke();
  ctx.strokeStyle = 'rgba(120,60,0,0.5)';
  ctx.lineWidth = br * 0.11;
  ctx.beginPath(); ctx.ellipse(0, 0, br * 1.75, br * 0.55, 0, 0, Math.PI * 2); ctx.stroke();
  ctx.restore();
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.beginPath(); ctx.arc(bx - br * 0.35, by - br * 0.35, br * 0.09, 0, Math.PI * 2); ctx.fill();

  // Stars — dense field with size/brightness distribution
  for (let i = 0; i < starCount; i++) {
    const x = Math.random() * size * 2;
    const y = Math.random() * size;
    const k = Math.random();
    const r = k < 0.04 ? 2.0 : k < 0.25 ? 1.2 : 0.65;
    const a = 0.35 + Math.random() * 0.65;
    const warm = Math.random() < 0.15, blue = Math.random() < 0.2;
    ctx.fillStyle = warm ? `rgba(255,220,180,${a})` : blue ? `rgba(180,200,255,${a})` : `rgba(225,235,255,${a})`;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }
  for (let i = 0; i < 34; i++) {
    const x = Math.random() * size * 2;
    const y = Math.random() * size * 0.9;
    const rg2 = ctx.createRadialGradient(x, y, 0, x, y, 16);
    rg2.addColorStop(0, 'rgba(255,255,255,0.95)');
    rg2.addColorStop(0.2, 'rgba(200,230,255,0.5)');
    rg2.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = rg2;
    ctx.fillRect(x - 16, y - 16, 32, 32);
    // cross flare
    ctx.strokeStyle = 'rgba(220,235,255,0.35)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x - 22, y); ctx.lineTo(x + 22, y); ctx.moveTo(x, y - 22); ctx.lineTo(x, y + 22); ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.anisotropy = maxAniso;

  const pmrem = new THREE.PMREMGenerator(renderer);
  const envMap = pmrem.fromEquirectangular(texture).texture;
  pmrem.dispose();

  const sky = new THREE.Mesh(
    new THREE.SphereGeometry(900, touch ? 40 : 64, touch ? 24 : 40),
    new THREE.MeshBasicMaterial({ map: texture, side: THREE.BackSide, fog: false })
  );
  sky.name = 'SpaceSky';
  sky.frustumCulled = false;

  return { sky, envMap };
}
